import { useComputed, useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { BankQuestion } from "../../types";
import { IcoSpinner, IcoStar } from "../icons";
import { SlideOver } from "./slide-over";

type QuestionBankPickerProps = {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  onAdd: (questions: BankQuestion[]) => void;
  onCreateCustom?: () => void;
};

export function QuestionBankPicker({
  open,
  onClose,
  workspaceId,
  onAdd,
  onCreateCustom,
}: QuestionBankPickerProps) {
  const questions = useSignal<BankQuestion[]>([]);
  const categories = useSignal<string[]>([]);
  const isLoading = useSignal(true);
  const selectedIds = useSignal<Set<string>>(new Set());
  const categoryFilter = useSignal<string | null>(null);
  const searchQuery = useSignal("");

  // Fetch questions when opened
  useEffect(() => {
    if (!open) {
      // Reset state when closed
      selectedIds.value = new Set();
      searchQuery.value = "";
      categoryFilter.value = null;
      return;
    }

    async function fetchQuestions() {
      isLoading.value = true;
      try {
        const { rpc } = await import("../../rpc");
        const [questionsResult, categoriesResult] = await Promise.all([
          rpc.questionBank.list({ workspaceId, includeArchived: false }),
          rpc.questionBank.getCategories({ workspaceId }),
        ]);
        questions.value = questionsResult.questions;
        categories.value = categoriesResult.categories;
      } catch (err) {
        console.error("Failed to load question bank:", err);
      } finally {
        isLoading.value = false;
      }
    }

    fetchQuestions();
  }, [open, workspaceId]);

  // Filter questions by category and search
  const filteredQuestions = useComputed(() => {
    let result = questions.value;

    if (categoryFilter.value) {
      result = result.filter((q) => q.category === categoryFilter.value);
    }

    if (searchQuery.value.trim()) {
      const query = searchQuery.value.toLowerCase();
      result = result.filter(
        (q) =>
          q.title.toLowerCase().includes(query) ||
          q.description.toLowerCase().includes(query),
      );
    }

    return result;
  });

  const selectedCount = useComputed(() => selectedIds.value.size);

  function toggleSelect(id: string) {
    const newSet = new Set(selectedIds.value);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    selectedIds.value = newSet;
  }

  function handleAdd() {
    const selected = questions.value.filter((q) => selectedIds.value.has(q.id));
    onAdd(selected);
    onClose();
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Add from Question Bank"
      footer={
        <div class="flex items-center justify-between">
          <span class="text-sm text-text-muted">
            {selectedCount.value} selected
          </span>
          <div class="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              class="px-4 py-2 rounded-lg text-sm font-medium text-text-muted hover:bg-surface-hover transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleAdd}
              disabled={selectedCount.value === 0}
              class="px-4 py-2 rounded-lg text-sm font-medium bg-text text-surface hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add {selectedCount.value > 0 ? `(${selectedCount.value})` : ""}
            </button>
          </div>
        </div>
      }
    >
      {isLoading.value ? (
        <div class="flex items-center justify-center py-12">
          <IcoSpinner class="size-8 animate-spin text-text-muted" />
        </div>
      ) : questions.value.length === 0 ? (
        <EmptyState workspaceId={workspaceId} />
      ) : (
        <div class="flex flex-col gap-4">
          {/* Search */}
          <input
            type="text"
            value={searchQuery.value}
            onInput={(e) => {
              searchQuery.value = (e.target as HTMLInputElement).value;
            }}
            placeholder="Search questions..."
            class="w-full px-3 py-2 rounded-lg border border-border bg-surface-hover focus:outline-none focus:ring-2 focus:ring-text-subtle text-text placeholder:text-text-subtle"
          />

          {/* Category filters */}
          {categories.value.length > 0 && (
            <div class="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => {
                  categoryFilter.value = null;
                }}
                class={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  categoryFilter.value === null
                    ? "bg-text text-surface"
                    : "bg-surface-hover text-text-muted hover:text-text"
                }`}
              >
                All
              </button>
              {categories.value.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    categoryFilter.value = cat;
                  }}
                  class={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    categoryFilter.value === cat
                      ? "bg-text text-surface"
                      : "bg-surface-hover text-text-muted hover:text-text"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          )}

          {/* Questions list */}
          {filteredQuestions.value.length === 0 ? (
            <p class="text-center text-text-muted py-8">
              No questions match your search
            </p>
          ) : (
            <div class="flex flex-col gap-2">
              {filteredQuestions.value.map((q) => (
                <QuestionItem
                  key={q.id}
                  question={q}
                  selected={selectedIds.value.has(q.id)}
                  onToggle={() => toggleSelect(q.id)}
                />
              ))}
            </div>
          )}

          {/* Create custom question option */}
          {onCreateCustom && (
            <div class="mt-6 pt-4 border-t border-border">
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onCreateCustom();
                }}
                class="w-full text-center text-sm text-text-muted hover:text-text transition-colors"
              >
                Need something specific?{" "}
                <span class="font-medium underline">
                  Create a custom question
                </span>
              </button>
            </div>
          )}
        </div>
      )}
    </SlideOver>
  );
}

function QuestionItem({
  question,
  selected,
  onToggle,
}: {
  question: BankQuestion;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      class={`flex items-start gap-3 p-3 rounded-xl text-left transition-colors ${
        selected
          ? "bg-blue-50 dark:bg-blue-950 border-2 border-blue-500"
          : "bg-surface-hover hover:bg-surface-active border-2 border-transparent"
      }`}
    >
      {/* Checkbox */}
      <div
        class={`mt-0.5 size-5 rounded flex items-center justify-center flex-shrink-0 ${
          selected ? "bg-blue-500 text-white" : "border-2 border-text-subtle"
        }`}
      >
        {selected && (
          <svg
            class="size-3"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
      </div>

      {/* Question info */}
      <div class="flex-1 min-w-0">
        <div class="font-medium text-text">{question.title}</div>
        {question.description && (
          <div class="text-sm text-text-muted mt-0.5 line-clamp-2">
            {question.description}
          </div>
        )}
        <div class="flex items-center gap-2 mt-1.5 text-xs text-text-subtle">
          {question.type === "rating" ? (
            <>
              <IcoStar class="size-3.5" />
              <span>Rating 1-{question.ratingMax}</span>
            </>
          ) : (
            <span>Short answer</span>
          )}
          {question.category && (
            <>
              <span>&middot;</span>
              <span>{question.category}</span>
            </>
          )}
        </div>
      </div>
    </button>
  );
}

function EmptyState({ workspaceId }: { workspaceId: string }) {
  return (
    <div class="text-center py-12">
      <IcoStar class="size-12 mx-auto text-text-subtle mb-4" />
      <p class="text-text-muted mb-4">
        Your question bank is empty. Create reusable questions to add them here.
      </p>
      <a
        href={`/orgs/${workspaceId}/forms/bank`}
        class="inline-flex items-center gap-2 text-sm font-medium text-text hover:text-text-secondary"
      >
        Go to Question Bank
      </a>
    </div>
  );
}
